import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Theme Store - Zustand store для управления темой
 * @contract Управление dark/light темой с персистентностью
 * @ensures Тема сохраняется в localStorage и применяется при загрузке
 */

export type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeState {
  // Состояние
  mode: ThemeMode;
  resolvedTheme: 'light' | 'dark';

  // Действия
  setMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
}

/**
 * Определяет системную тему через media query
 */
function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Применяет тему к document.documentElement
 */
function applyTheme(theme: 'light' | 'dark') {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

/**
 * Резолвит режим темы в конкретную тему
 */
function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    return getSystemTheme();
  }
  return mode;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      // Начальное состояние
      mode: 'system',
      resolvedTheme: getSystemTheme(),

      // Установка режима темы
      setMode: (mode: ThemeMode) => {
        const resolvedTheme = resolveTheme(mode);
        applyTheme(resolvedTheme);
        set({ mode, resolvedTheme });
      },

      // Быстрое переключение light/dark
      toggleTheme: () => {
        const currentResolved = get().resolvedTheme;
        const newMode: ThemeMode = currentResolved === 'dark' ? 'light' : 'dark';
        const resolvedTheme = resolveTheme(newMode);
        applyTheme(resolvedTheme);
        set({ mode: newMode, resolvedTheme });
      },
    }),
    {
      name: 'theme-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        mode: state.mode,
      }),
      onRehydrateStorage: () => (state) => {
        // После загрузки из localStorage применяем тему
        if (state) {
          const resolvedTheme = resolveTheme(state.mode);
          applyTheme(resolvedTheme);
          state.resolvedTheme = resolvedTheme;
        }
      },
    },
  ),
);

// Слушаем изменения системной темы
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    const state = useThemeStore.getState();
    if (state.mode === 'system') {
      const newTheme = e.matches ? 'dark' : 'light';
      applyTheme(newTheme);
      useThemeStore.setState({ resolvedTheme: newTheme });
    }
  });
}

// Вспомогательные хуки
export function useTheme() {
  return useThemeStore((state) => state.resolvedTheme);
}

export function useThemeMode() {
  return useThemeStore((state) => state.mode);
}

export function useIsDarkMode() {
  return useThemeStore((state) => state.resolvedTheme === 'dark');
}
